import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Upload, Search, Layers } from "lucide-react";
import api, { formatRp, imageUrl, formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import ProductVideoField from "@/components/admin/ProductVideoField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useIsFeatureEnabled } from "@/hooks/useFeatureFlag";
import VariantManager from "@/components/admin/variants/VariantManager";
import { toVariantPayload, validateOptionGroups, validateVariantRow, comboLabel } from "@/components/admin/variants/variantHelpers";

const empty = {
  name: "", slug: "", category_id: "", description: "",
  price: 0, sale_price: null, cost_price: 0, stock: 0,
  weight_grams: 0, unit: "", sku: "", images: [],
  is_active: true, is_featured: false, is_bestseller: false,
  has_variants: false, option_groups: [],
};

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function unitLabel(u) {
  return u.length <= 3 ? u.toUpperCase() : u.charAt(0).toUpperCase() + u.slice(1);
}

// Ukuran kontrol form produk (scoped — tidak menyentuh components/ui global)
const FIELD = "h-12 px-4 text-[15px] rounded-xl";
const FIELD_LABEL = "mb-1.5 block text-sm font-medium text-[#1A1A1A]";

function ProductForm({ initial, categories, unitInfo, onSaved }) {
  const variantsFeatureEnabled = useIsFeatureEnabled("core.product_variants");
  const [form, setForm] = useState(() => (
    initial
      ? {
          ...empty,
          ...initial,
          unit: (initial.unit || "").toLowerCase(),
          has_variants: !!initial.has_variants,
          option_groups: Array.isArray(initial.option_groups) ? initial.option_groups : [],
        }
      : empty
  ));
  const [uploading, setUploading] = useState(false);
  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingDisableVariants, setPendingDisableVariants] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Load existing variants when editing a variant-enabled product
  useEffect(() => {
    if (!initial?.id || !initial?.has_variants || !variantsFeatureEnabled) return;
    let cancelled = false;
    setVariantsLoading(true);
    api.get(`/products/${initial.id}/variants`, { params: { include_inactive: true } })
      .then((r) => {
        if (cancelled) return;
        setVariants((r.data?.items || []).map((v) => ({ ...v, _matched: true })));
      })
      .catch((e) => { if (!cancelled) notifyError(e); })
      .finally(() => { if (!cancelled) setVariantsLoading(false); });
    return () => { cancelled = true; };
  }, [initial?.id, initial?.has_variants, variantsFeatureEnabled]);

  // Produk baru: default ke satuan efektif tenant (derived — bebas timing fetch)
  const effectiveUnit = form.unit || (!initial?.id ? unitInfo.default : "") || "";

  const unitOptions = React.useMemo(() => {
    const opts = (unitInfo.units || []).map((u) => ({ value: u, label: unitLabel(u), legacy: false }));
    const cur = (effectiveUnit || "").toLowerCase();
    if (cur && !opts.some((o) => o.value === cur)) {
      opts.push({ value: cur, label: `${unitLabel(cur)} (lama)`, legacy: true });
    }
    return opts;
  }, [unitInfo.units, effectiveUnit]);
  const isLegacyUnit = unitOptions.some((o) => o.legacy && o.value === effectiveUnit);

  const handleToggleVariants = (v) => {
    if (!v && form.has_variants && (form.option_groups?.length || variants.length)) {
      setPendingDisableVariants(true);
      return;
    }
    upd("has_variants", v);
    if (v && form.option_groups?.length === 0) {
      upd("option_groups", []);
    }
  };

  const confirmDisableVariants = () => {
    setPendingDisableVariants(false);
    setForm((s) => ({ ...s, has_variants: false, option_groups: [] }));
    setVariants([]);
  };

  const validateBeforeSubmit = () => {
    if (form.has_variants) {
      const groupErrors = validateOptionGroups(form.option_groups);
      if (groupErrors.length) {
        toast.error(groupErrors[0]);
        return false;
      }
      if (!variants.length) {
        toast.error("Generate kombinasi varian terlebih dahulu.");
        return false;
      }
      for (const row of variants) {
        const errs = validateVariantRow(row, { images: form.images });
        const first = Object.values(errs)[0];
        if (first) {
          toast.error(`${comboLabel(row.option_values)}: ${first}`);
          return false;
        }
      }
      // duplicate SKU (client-side pre-check; server enforces hard uniqueness per tenant)
      const seen = new Set();
      for (const row of variants) {
        const sku = (row.sku || "").trim();
        if (!sku) continue;
        if (seen.has(sku)) {
          toast.error(`SKU duplikat di grid: ${sku}`);
          return false;
        }
        seen.add(sku);
      }
    }
    return true;
  };

  /**
   * Sync variants after product PUT/POST. Handles create/update/soft-delete.
   * Partial-failure aware: continues on error, collects per-row errors, returns summary.
   */
  const syncVariants = async (productId) => {
    // Load current server variants to compute deletions (only reliable for edit path)
    let serverRows = [];
    try {
      const r = await api.get(`/products/${productId}/variants`, { params: { include_inactive: true } });
      serverRows = r.data?.items || [];
    } catch (_) { /* new product path — treat as empty */ }
    const serverById = new Map(serverRows.map((v) => [v.id, v]));
    const keepIds = new Set(variants.filter((v) => v.id).map((v) => v.id));
    const toDelete = serverRows.filter((v) => !keepIds.has(v.id));
    const errors = [];
    let created = 0, updated = 0, deleted = 0;

    // Deletions first — reduces SKU/combination collisions when reshuffling
    for (const v of toDelete) {
      try { await api.delete(`/variants/${v.id}`); deleted += 1; }
      catch (e) { errors.push(`Hapus ${comboLabel(v.option_values)}: ${formatApiError(e)}`); }
    }
    for (const row of variants) {
      const payload = toVariantPayload(row);
      try {
        if (row.id && serverById.has(row.id)) {
          await api.put(`/variants/${row.id}`, payload);
          updated += 1;
        } else {
          await api.post(`/products/${productId}/variants`, payload);
          created += 1;
        }
      } catch (e) {
        errors.push(`${comboLabel(row.option_values)}: ${formatApiError(e)}`);
      }
    }
    return { created, updated, deleted, errors };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!validateBeforeSubmit()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      payload.price = Number(payload.price);
      payload.sale_price = payload.sale_price === "" || payload.sale_price === null ? null : Number(payload.sale_price);
      payload.cost_price = Number(payload.cost_price);
      payload.stock = Number(payload.stock);
      payload.weight_grams = Number(payload.weight_grams);
      if (!payload.slug) payload.slug = slugify(payload.name);
      // Ensure option_groups sent only when has_variants ON
      if (!payload.has_variants) payload.option_groups = [];
      // Video produk dikelola endpoint terpisah (PUT produk tidak boleh menyentuhnya)
      delete payload.product_video;

      let productId = initial?.id;
      if (productId) {
        await api.put(`/products/${productId}`, payload);
      } else {
        const { data } = await api.post("/products", payload);
        productId = data?.id;
      }

      if (payload.has_variants && productId && variantsFeatureEnabled) {
        const result = await syncVariants(productId);
        if (result.errors.length) {
          toast.error(`Produk tersimpan, tapi ${result.errors.length} varian gagal disinkronkan.`);
          result.errors.slice(0, 3).forEach((msg) => toast.error(msg));
        } else {
          toast.success(`Produk & ${result.created + result.updated} varian tersimpan.`);
        }
      } else {
        toast.success("Produk tersimpan");
      }
      onSaved?.();
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  };

  const uploadImg = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("images", [...(form.images || []), data.path]);
    } catch (e) { notifyError(e); } finally { setUploading(false); }
  };

  const simplePriceStockDisabled = form.has_variants;
  const Fallback = () => (simplePriceStockDisabled ? <span className="text-[11px] text-[#5C5C5C] font-normal"> · fallback</span> : null);

  return (
    <>
    <form onSubmit={submit} className="space-y-5" data-testid="product-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
        <div className="md:col-span-2">
          <Label className={FIELD_LABEL}>Nama Produk</Label>
          <Input value={form.name} onChange={(e) => upd("name", e.target.value)} required className={FIELD} data-testid="prod-name" />
        </div>
        <div>
          <Label className={FIELD_LABEL}>Slug URL</Label>
          <Input value={form.slug} onChange={(e) => upd("slug", e.target.value)} placeholder={slugify(form.name)} className={FIELD} data-testid="prod-slug" />
        </div>
        <div>
          <Label className={FIELD_LABEL}>Kategori</Label>
          <Select value={form.category_id} onValueChange={(v) => upd("category_id", v)}>
            <SelectTrigger className={FIELD} data-testid="prod-category"><SelectValue placeholder="Pilih..." /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className={FIELD_LABEL}>Deskripsi</Label>
        <Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={5} className="min-h-[120px] px-4 py-3 text-[15px] rounded-xl resize-y" data-testid="prod-desc" />
      </div>

      {variantsFeatureEnabled && (
        <div className="bg-[#FAF9F6] border border-[#E5E5E5] rounded-xl p-4 flex items-center justify-between gap-4" data-testid="has-variants-toggle-row">
          <div className="flex items-center gap-3">
            <Layers size={18} className="text-[#5C5C5C] shrink-0" />
            <div>
              <div className="text-sm font-semibold text-[#1A1A1A]">Produk ini memiliki varian?</div>
              <div className="text-xs text-[#5C5C5C] mt-0.5">Aktifkan untuk mengelola SKU/harga/stok per kombinasi (mis. warna × ukuran).</div>
            </div>
          </div>
          <Switch
            checked={!!form.has_variants}
            onCheckedChange={handleToggleVariants}
            data-testid="has-variants-toggle"
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
        <div>
          <Label className={FIELD_LABEL}>Harga (Rp)<Fallback /></Label>
          <Input type="number" value={form.price} onChange={(e) => upd("price", e.target.value)} required className={FIELD} data-testid="prod-price" />
        </div>
        <div>
          <Label className={FIELD_LABEL}>Harga Diskon</Label>
          <Input type="number" value={form.sale_price ?? ""} onChange={(e) => upd("sale_price", e.target.value)} placeholder="-" className={FIELD} data-testid="prod-sale" />
        </div>
        <div>
          <Label className={FIELD_LABEL}>Harga Modal</Label>
          <Input type="number" value={form.cost_price ?? 0} onChange={(e) => upd("cost_price", e.target.value)} className={FIELD} data-testid="prod-cost" />
        </div>
        <div>
          <Label className={FIELD_LABEL}>Stok<Fallback /></Label>
          <Input type="number" value={form.stock} onChange={(e) => upd("stock", e.target.value)} required className={FIELD} data-testid="prod-stock" />
        </div>
        <div>
          <Label className={FIELD_LABEL}>Berat (gr)</Label>
          <Input type="number" value={form.weight_grams} onChange={(e) => upd("weight_grams", e.target.value)} className={FIELD} data-testid="prod-weight" />
        </div>
        <div>
          <Label className={FIELD_LABEL}>Satuan</Label>
          <Select value={effectiveUnit} onValueChange={(v) => upd("unit", v)}>
            <SelectTrigger className={FIELD} data-testid="prod-unit"><SelectValue placeholder="Pilih..." /></SelectTrigger>
            <SelectContent>
              {unitOptions.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`prod-unit-opt-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isLegacyUnit && (
            <div className="text-xs text-amber-600 mt-1.5" data-testid="prod-unit-legacy-warning">
              Satuan lama — tidak ada di konfigurasi template saat ini.
            </div>
          )}
        </div>
        <div className="md:col-span-2">
          <Label className={FIELD_LABEL}>SKU<Fallback /></Label>
          <Input value={form.sku || ""} onChange={(e) => upd("sku", e.target.value)} className={FIELD} data-testid="prod-sku" />
        </div>
      </div>
      <div>
        <Label className={FIELD_LABEL}>Gambar Produk</Label>
        <div className="flex gap-3 flex-wrap" data-testid="prod-images">
          {form.images?.map((img, i) => (
            <div key={`${img}-${i}`} className="w-24 h-24 relative rounded-xl overflow-hidden border border-[#E5E5E5]" data-testid={`prod-image-${i}`}>
              <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => upd("images", form.images.filter((_, idx) => idx !== i))} className="absolute top-1.5 right-1.5 bg-white/95 rounded-full p-1 text-[#DC2F02] shadow-sm" aria-label="Hapus gambar" data-testid={`prod-image-remove-${i}`}><Trash2 size={14} /></button>
              <div className="absolute bottom-0 left-0 right-0 text-[10px] text-white bg-black/50 text-center py-0.5">#{i + 1}</div>
            </div>
          ))}
          <label className="w-24 h-24 rounded-xl border-2 border-dashed border-[#E5E5E5] grid place-items-center cursor-pointer hover:bg-[#FAF9F6] text-[#5C5C5C]" data-testid="prod-upload-area">
            <div className="grid place-items-center gap-1"><Upload size={22} /><span className="text-[11px]">Upload</span></div>
            <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadImg(e.target.files[0])} data-testid="prod-upload-img" />
          </label>
        </div>
        {uploading && <div className="text-xs text-[#5C5C5C] mt-1.5">Mengupload...</div>}
      </div>

      <ProductVideoField
        productId={initial?.id}
        value={form.product_video}
        posterSrc={form.images?.[0] ? imageUrl(form.images[0]) : ""}
        onChange={(v) => upd("product_video", v)}
        disabled={saving}
      />

      {variantsFeatureEnabled && form.has_variants && (
        <>
          {variantsLoading ? (
            <div className="text-xs text-[#5C5C5C] italic p-3 border border-[#E5E5E5] rounded-xl" data-testid="variants-loading">Memuat varian...</div>
          ) : (
            <VariantManager
              optionGroups={form.option_groups}
              onOptionGroupsChange={(v) => upd("option_groups", v)}
              variants={variants}
              onVariantsChange={setVariants}
              images={form.images}
              defaults={{ price: form.price, cost_price: form.cost_price, stock: form.stock, weight_grams: form.weight_grams }}
              disabled={saving}
            />
          )}
        </>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
        <Label className="flex items-center gap-3 min-h-[44px] px-4 rounded-xl border border-[#E5E5E5] bg-[#FAF9F6] cursor-pointer text-sm" data-testid="prod-active-toggle"><Switch checked={form.is_active} onCheckedChange={(v) => upd("is_active", v)} /> Aktif</Label>
        <Label className="flex items-center gap-3 min-h-[44px] px-4 rounded-xl border border-[#E5E5E5] bg-[#FAF9F6] cursor-pointer text-sm" data-testid="prod-featured-toggle"><Switch checked={form.is_featured} onCheckedChange={(v) => upd("is_featured", v)} /> Unggulan</Label>
        <Label className="flex items-center gap-3 min-h-[44px] px-4 rounded-xl border border-[#E5E5E5] bg-[#FAF9F6] cursor-pointer text-sm" data-testid="prod-bestseller-toggle"><Switch checked={form.is_bestseller} onCheckedChange={(v) => upd("is_bestseller", v)} /> Terlaris</Label>
      </div>
      <DialogFooter className="pt-3">
        <Button type="submit" disabled={saving} className="brand-btn-primary rounded-xl h-12 px-6 text-[15px] w-full sm:w-auto" data-testid="prod-save-btn">
          {saving ? "Menyimpan..." : "Simpan Produk"}
        </Button>
      </DialogFooter>
    </form>

    <AlertDialog open={pendingDisableVariants} onOpenChange={(o) => !o && setPendingDisableVariants(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Matikan varian?</AlertDialogTitle>
          <AlertDialogDescription>
            Menonaktifkan opsi ini akan menghapus semua kombinasi varian saat Anda menyimpan produk. Grup opsi juga akan dikosongkan. Lanjutkan?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="disable-variants-cancel">Batal</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDisableVariants} className="bg-[#DC2F02] hover:bg-[#b02701]" data-testid="disable-variants-confirm">
            Ya, matikan
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [unitInfo, setUnitInfo] = useState({ units: [], default: "" });

  const load = useCallback(() => {
    api.get("/products", { params: { include_inactive: true, limit: 200, search: search || undefined } })
      .then((r) => setProducts(r.data.items));
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);
  useEffect(() => { api.get("/product-units").then((r) => setUnitInfo(r.data)).catch(() => {}); }, []);

  const confirmDelete = async () => {
    if (!toDelete?.id) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/products/${toDelete.id}`);
      // Optimistic UI update: remove from local list immediately
      setProducts((prev) => prev.filter((p) => p.id !== toDelete.id));
      const msg = data?.delete_type === "soft"
        ? "Produk berhasil dihapus (soft delete — terkait riwayat pesanan)"
        : "Produk berhasil dihapus";
      toast.success(msg);
      setToDelete(null);
      // Refresh from server to ensure consistency
      load();
    } catch (e) {
      toast.error(`Gagal menghapus produk: ${formatApiError(e)}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black">Produk</h1>
          <p className="text-sm text-[#5C5C5C]">{products.length} produk</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild><Button className="brand-btn-primary rounded-xl" data-testid="add-product-btn" data-tour="product-add-btn"><Plus size={14} className="mr-1" /> Tambah Produk</Button></DialogTrigger>
          <DialogContent className="max-w-5xl xl:max-w-6xl max-h-[92vh] overflow-y-auto p-5 sm:p-7">
            <DialogHeader className="mb-1"><DialogTitle className="text-xl">{editing ? "Edit" : "Tambah"} Produk</DialogTitle></DialogHeader>
            <ProductForm initial={editing} categories={categories} unitInfo={unitInfo} onSaved={() => { load(); setOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-2xl">
        <div className="p-4 border-b border-[#E5E5E5]">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama produk..." className="pl-9 rounded-xl" data-testid="prod-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b border-[#E5E5E5]">
                <th className="px-4 py-3">Produk</th>
                <th className="px-4 py-3">Harga</th>
                <th className="px-4 py-3">Stok</th>
                <th className="px-4 py-3">Terjual</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FAF9F6]" data-testid={`prod-row-${p.slug}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#FAF9F6] overflow-hidden shrink-0">
                        {p.images?.[0] && <img src={imageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold line-clamp-1 flex items-center gap-1.5">
                          {p.name}
                          {p.has_variants && (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-[#E85D04]/10 text-[#E85D04] rounded-full px-1.5 py-0.5"
                              data-testid={`prod-variant-badge-${p.slug}`}
                            >
                              <Layers size={9} /> Varian
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#5C5C5C]">{p.sku || "-"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-bold text-[#E85D04]">
                    {formatRp(p.sale_price || p.price)}
                    {p.sale_price && <div className="text-xs text-[#5C5C5C] line-through font-normal">{formatRp(p.price)}</div>}
                  </td>
                  <td className="px-4 py-3">{p.stock} {p.unit}</td>
                  <td className="px-4 py-3">{p.sold_count || 0}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${p.is_active ? "bg-[#2D6A4F]/10 text-[#2D6A4F]" : "bg-red-100 text-red-700"}`}>
                      {p.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }} className="rounded-lg" data-testid={`edit-prod-${p.slug}`}><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setToDelete(p)} className="rounded-lg text-[#DC2F02] hover:bg-red-50 hover:text-[#DC2F02]" data-testid={`del-prod-${p.slug}`}><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && !deleting && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus produk?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda akan menghapus <b>{toDelete?.name}</b>. Jika produk pernah dipesan, data akan disimpan
              sebagai arsip (soft delete) untuk menjaga riwayat pesanan, tetapi tidak akan tampil lagi
              di mana pun. Tindakan ini akan dicatat ke audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="confirm-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-[#DC2F02] hover:bg-[#b02701]"
              data-testid="confirm-delete-ok"
            >
              {deleting ? "Menghapus..." : "Ya, hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
